/**
 * The account API: signing in, the roster, and creating a character.
 *
 * ── Why there is no token in this file ──────────────────────────────────────
 *
 * **The session is an `HttpOnly` cookie, so this code cannot read it and does not try.**
 * Every call passes `credentials: 'include'` and the browser attaches it; there is nothing
 * to store, nothing to refresh by hand, and — the point — nothing for an XSS to steal. A
 * token in `localStorage` would be readable by any script on the page, so one bad
 * dependency or one unescaped item name would hand over every logged-in session
 * permanently.
 *
 * The consequence for this module is that "am I signed in" is a *question for the server*
 * rather than a local check. `me()` is that question, and it is what the boot sequence
 * asks before drawing anything.
 */

import { apiBase } from './mount';

/**
 * Where the API lives, derived from where the HUD is mounted rather than written down again.
 *
 * It was `/grind/api`, one of four hardcoded copies of the prefix — see `net/mount.ts` for why
 * that stopped being tenable the moment the game got its own subdomain.
 */
const BASE = apiBase();

export interface Account {
  id: string;
  username: string;
}

export interface RosterCharacter {
  id: string;
  name: string;
  league: string;
  colour: string;
  archetype: string;
  armourSet: string;
  createdAt: number;
  playedAt: number;
  /** Absent for a character saved by a build that did not record one — see below. */
  level?: number;
}

export interface LeagueChoice {
  id: string;
  name: string;
  note?: string;
  main?: boolean;
}

/**
 * The roster, plus everything the creator needs to offer.
 *
 * The colours, kits and leagues come **from the server** rather than from a table here.
 * They are validated server-side on the way back in, so a client list that drifted would
 * offer choices that are refused on submit — and the player would have no way to tell which
 * of their choices was the bad one.
 */
export interface Roster {
  characters: RosterCharacter[];
  max: number;
  colours: string[];
  archetypes: string[];
  armourSets: string[];
  leagues: LeagueChoice[];
}

/**
 * An error carrying what the server actually said.
 *
 * The message is shown to the player, so it has to be the server's: *that name is taken in
 * this league* is actionable and "request failed" is not. `status` is kept because 401
 * means something different from every other failure — it is the answer to *are you signed
 * in*, not a fault.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(BASE + path, {
    ...init,
    // Without this the cookie is not sent and every call is a 401 — the single most
    // confusing failure available here, because the browser *has* the cookie.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
  });

  const body = await response.json().catch(() => ({}) as Record<string, unknown>);
  if (!response.ok) {
    const message =
      typeof body.error === 'string' ? body.error : `That did not work (${response.status}).`;
    throw new ApiError(message, response.status);
  }
  return body as T;
}

/**
 * Who is signed in, or `null`.
 *
 * **The silent handshake.** The client calls this before it draws anything: a valid cookie
 * skips the login screen entirely and goes straight to the roster, which is what turns
 * *log in once* into *stay logged in*.
 *
 * A 401 is `null` rather than a thrown error, because "not signed in" is an ordinary answer
 * to this question and not a failure. Anything else does throw — a server that is down must
 * not look like a session that expired, or the player retypes a password that was fine.
 */
export async function me(): Promise<Account | null> {
  try {
    const { account } = await request<{ account: Account }>('/auth/me');
    return account;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

/**
 * The boot question, answered in **one** request.
 *
 * Three outcomes, and the third is not a failure:
 *
 *  - `{ enabled: true, account }` — signed in.
 *  - `{ enabled: true, account: null }` — accounts exist; this browser is not signed in.
 *  - `{ enabled: false }` — the server has no accounts at all.
 *
 * A server started with `GRIND_DB=""` registers no auth routes, so `/auth/me` **404s** —
 * which is a different thing from a 401 and must not be shown as one. That configuration is
 * the game as it was before accounts existed: anonymous characters, nothing saved, and it is
 * what the balance harness and a bare dev server run in.
 *
 * Detected rather than configured, because a build-time flag would be a second source of
 * truth for something the server already answers, and the two would drift the first time
 * somebody ran a dev build against a production server.
 *
 * **One call, not two.** This began as `accountsEnabled()` followed by `me()`, which asked
 * the same endpoint the same question twice on every page load — visible as paired 401s in
 * the server log. The three outcomes all come off one response, so there was never a reason
 * for the second.
 */
export interface BootState {
  enabled: boolean;
  account: Account | null;
}

export async function bootState(): Promise<BootState> {
  try {
    const { account } = await request<{ account: Account }>('/auth/me');
    return { enabled: true, account };
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 404) return { enabled: false, account: null };
      if (error.status === 401) return { enabled: true, account: null };
    }
    // A network failure is none of the three, and is rethrown. Treating it as "accounts are
    // off" would drop a player into a fresh anonymous character on a server that is holding
    // their real one.
    throw error;
  }
}

export async function login(username: string, password: string): Promise<Account> {
  const { account } = await request<{ account: Account }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  return account;
}

/**
 * Signs out.
 *
 * The server revokes the session and clears the cookie; this cannot do either itself, which
 * is the same `HttpOnly` property that makes the cookie worth using. **So a failed logout
 * must not look like a successful one** — the caller keeps the player signed in and says so,
 * rather than showing a login screen over a session that is still live.
 */
export async function logout(): Promise<void> {
  await request<{ ok: boolean }>('/auth/logout', { method: 'POST' });
}

export async function roster(): Promise<Roster> {
  return request<Roster>('/characters');
}

export interface NewCharacter {
  name: string;
  league: string;
  colour: string;
  archetype: string;
  armourSet: string;
}

export async function createCharacter(req: NewCharacter): Promise<RosterCharacter> {
  const { character } = await request<{ character: RosterCharacter }>('/characters', {
    method: 'POST',
    body: JSON.stringify(req),
  });
  return character;
}

export async function deleteCharacter(id: string): Promise<void> {
  await request<{ ok: boolean }>(`/characters/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
